import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CalendarDays, Briefcase, Link2, Activity, ChevronRight } from 'lucide-react';
import Footer from '../components/Footer';
import { Header } from '../components/Header';
import Seo from '../components/Seo';
import { useI18n } from '../i18n';

// Portuguese landing page (/pt), paired with / through hreflang. Visitors who land here
// get the game in Portuguese from the first screen.
const FAQ = [
  { q: 'O My Football Game é grátis?', a: 'Sim. Podes jogar tudo sem pagar. Existe uma moeda premium opcional, mas nunca precisas dela para competir.' },
  { q: 'Preciso de instalar alguma coisa?', a: 'Não. O jogo corre no browser, no telemóvel, tablet ou computador. Não há downloads nem instalação.' },
  { q: 'Como funciona uma época?', a: 'Jogas uma liga de 10 clubes em 18 jornadas, a duas voltas. Os dois primeiros sobem de divisão e os dois últimos descem. Há cinco divisões, da Liga Amadora à Liga de Elite.' },
  { q: 'A direção pode despedir-me?', a: 'Não te despede, mas exige resultados. Se a confiança da direção cair abaixo de 20, recebes um ultimato: ganhar dois dos três jogos seguintes. Se falhares, perdes o prémio da época.' },
  { q: 'Posso jogar contra amigos?', a: 'Sim. Envia o teu link de desafio pelo WhatsApp: o teu amigo joga contra o teu clube logo ali, sem criar conta. Cada jogo entre vocês conta para o vosso confronto direto.' },
];

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  inLanguage: 'pt',
  mainEntity: FAQ.map(({ q, a }) => ({ '@type': 'Question', name: q, acceptedAnswer: { '@type': 'Answer', text: a } })),
};

const FEATURES = [
  { icon: CalendarDays, title: 'Épocas completas', text: '18 jornadas contra nove clubes rivais, com subidas e descidas em cinco divisões e um prémio no fim de cada época.' },
  { icon: Briefcase, title: 'Uma direção exigente', text: 'Antes de cada época a direção define o objetivo: título, subida, metade de cima ou manutenção. Cada resultado conta.' },
  { icon: Activity, title: 'Jogos ao vivo', text: 'Acompanha cada jogo minuto a minuto, com posse de bola, remates, golos esperados (xG) e notas dos jogadores.' },
  { icon: Link2, title: 'Desafia amigos com um link', text: 'O teu amigo joga contra o teu clube sem criar conta. Depois, o confronto direto entre vocês fica registado.' },
];

export default function LandingPagePt() {
  const { setLang } = useI18n();
  useEffect(() => { setLang('pt'); }, [setLang]);

  return (
    <div className="min-h-[100dvh]" style={{ background: 'var(--surface-0)', color: 'var(--text-1)' }}>
      <Seo
        title="Jogo de Manager de Futebol Online Grátis | My Football Game"
        description="Cria o teu clube, escolhe a tática e joga uma época completa: subidas, descidas e uma direção que avalia cada resultado. Grátis, no browser, sem downloads."
        path="/pt"
        lang="pt"
        alternates={[['en', '/'], ['pt', '/pt'], ['es', '/es'], ['x-default', '/']]}
        jsonLd={faqJsonLd}
      />
      <div className="container mx-auto px-4">
        <Header />
      </div>

      <main className="max-w-4xl mx-auto px-4 pb-16">
        <section className="text-center pt-6 pb-14">
          <div className="pc-eyebrow pc-eyebrow-accent mb-3">My Football Game · Grátis</div>
          <h1 className="pc-display text-5xl md:text-7xl leading-[0.9] mb-5">
            O TEU CLUBE.<br /><span style={{ color: 'var(--accent)' }}>AS TUAS DECISÕES.</span>
          </h1>
          <p className="text-base md:text-lg text-[var(--text-2)] max-w-2xl mx-auto mb-8 leading-relaxed">
            Um jogo de manager de futebol online e grátis. Escolhe o onze, define a tática, contrata no mercado de transferências
            e luta pela subida de divisão. Joga no browser, sem downloads.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/register" className="px-7 py-3 rounded font-bold text-[13px] uppercase tracking-[0.2em] inline-flex items-center gap-2"
              style={{ background: 'var(--accent)', color: 'var(--surface-0)', boxShadow: '0 0 24px var(--accent-glow)' }}>
              Criar o meu clube <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/login" className="px-7 py-3 rounded font-bold text-[13px] uppercase tracking-[0.2em]"
              style={{ background: 'var(--surface-2)', color: 'var(--text-1)' }}>
              Entrar
            </Link>
          </div>
          <p className="text-xs text-[var(--text-3)] mt-4">Demora cerca de 30 segundos, com Google ou email.</p>
        </section>

        <section className="grid gap-4 md:grid-cols-2 mb-16" aria-label="Funcionalidades">
          {FEATURES.map(({ icon: Icon, title, text }) => (
            <div key={title} className="rounded-xl p-6" style={{ background: 'var(--surface-1)', border: '1px solid var(--border-edge)' }}>
              <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3" style={{ background: 'rgba(184,255,61,0.1)', color: 'var(--accent)' }}>
                <Icon className="w-5 h-5" />
              </div>
              <h2 className="text-lg font-bold mb-2">{title}</h2>
              <p className="text-sm text-[var(--text-3)] leading-relaxed">{text}</p>
            </div>
          ))}
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold mb-4">Como começar</h2>
          <ol className="space-y-3 text-[var(--text-2)] leading-relaxed list-decimal pl-5">
            <li><strong>Cria o teu clube</strong> — recebes um plantel completo e dinheiro para o mercado.</li>
            <li><strong>Escolhe a tática</strong> — 18 formações, mentalidade, pressão e marcadores de bolas paradas.</li>
            <li><strong>Joga a primeira jornada</strong> — o jogo é simulado ao vivo, minuto a minuto.</li>
            <li><strong>Luta pela subida</strong> — os dois primeiros sobem de divisão no fim da época.</li>
          </ol>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold mb-6">Perguntas frequentes</h2>
          <div className="space-y-3">
            {FAQ.map((item) => (
              <details key={item.q} className="rounded-lg overflow-hidden group" style={{ background: 'var(--surface-1)', border: '1px solid var(--border-edge)' }}>
                <summary className="px-5 py-4 cursor-pointer font-bold text-sm list-none flex items-center justify-between">
                  {item.q}
                  <ChevronRight className="w-4 h-4 text-[var(--text-3)] transition-transform group-open:rotate-90" />
                </summary>
                <div className="px-5 pb-4 pt-3 text-sm text-[var(--text-2)] leading-relaxed" style={{ borderTop: '1px solid var(--border-edge)' }}>{item.a}</div>
              </details>
            ))}
          </div>
        </section>

        <p className="text-center text-sm text-[var(--text-3)]">
          Prefer English? <Link to="/" className="underline" style={{ color: 'var(--accent)' }}>Play in English</Link>
        </p>
      </main>

      <Footer />
    </div>
  );
}
